import Router from 'next/router';
import Script from 'next/script';

import { environment } from 'environment';
import type { H, HighlightOptions } from 'highlight.run';
import Cookie from 'js-cookie';
import type { ContactUsReason } from 'prismas';

const botAgentRegex =
  /bot|spider|crawl|slurp|curl|facebookexternal|fetch|google|linkedin|pingdom|yahoo|yandex|twitter/i;

const storage = {
  visitor: `b4k-visitor`,
  loggedIn: `${environment.cookies.prefix}b4k-logged-in`,
} as const;

export interface Umami {
  track(): void;
  track(name: string): void;
  track(name: string, data: object): void;
  track(props: (defaultProps: object) => object): void;
  track(event: string, data: object): void;
}

class FakeUmami implements Umami {
  track(name?: string): void;
  track(name: string, data: object): void;
  track(props: (defaultProps: object) => object): void;
  track(event: string, data: object): void;
  track(...args: [string | ((defaultProps: object) => object) | object, object?]): void {
    if (!args.length) return console.log('Umami track generic event');

    if (typeof args[0] === 'string' && !args[1]) return console.log('Umami track event named ', args[0]);
    if (typeof args[0] === 'string' && args[1])
      return console.log('Umami track event named ', args[0], ' with data ', args[1]);
    if (typeof args[0] === 'function') return console.log('Umami track event with props ', args[0]({}));
    if (typeof args[0] === 'object' && args[1])
      return console.log('Umami track event with props ', args[0], ' and data ', args[1]);
    if (typeof args[0] === 'object' && !args[1]) return console.log('Umami track event with props ', args[0]);

    console.log('Umami track event with unknown args ', args);
  }
}

class B4KTracker {
  private static instance: B4KTracker;
  private umami: Umami = new FakeUmami();
  private gtag = window.gtag;
  private h: typeof H | undefined = undefined;

  private highlightInitialized = false;
  private umamiInitialized = false;
  private gtagInitialized = false;

  constructor() {
    this.initUmami();
    void this.initHighlightListener();
  }

  /** @param amount in dollars */
  trackDonation(amount: number, txId: string) {
    this.umami.track('donation', { amount });

    return new Promise<void>(resolve =>
      this.gtag('event', 'conversion', {
        send_to: 'AW-10836102046/vxWECM_0hP8YEJ6Xh68o',
        value: amount,
        currency: 'USD',
        transaction_id: txId,
        event_callback: resolve,
      }),
    );
  }

  /** @param amount in dollars */
  trackSubscriptionDonation(amount: number, txId: string) {
    this.umami.track('subscription', { amount });

    return new Promise<void>(resolve =>
      this.gtag('event', 'conversion', {
        send_to: 'AW-10836102046/lb4bCNL0hP8YEJ6Xh68o',
        value: amount * 12, // 12 months of subscription estimate
        currency: 'USD',
        transaction_id: txId,
        event_callback: resolve,
      }),
    );
  }

  trackContactUs(reason: ContactUsReason) {
    this.umami.track('contact-us', { reason });

    return new Promise<void>(resolve =>
      this.gtag('event', 'conversion', {
        send_to: 'AW-10836102046/UeHCCPCohf8YEJ6Xh68o',
        event_callback: resolve,
      }),
    );
  }

  initUmami() {
    if (typeof window === 'undefined' || !window.umami || this.umamiInitialized) return;

    this.umami = window.umami;
    this.umamiInitialized = true;
  }

  handleGtagLoad() {
    if (typeof window === 'undefined' || this.gtagInitialized) return;

    this.gtag('js', new Date());
    this.gtag('config', 'G-WGMXKHWYT4');
    if (!botAgentRegex.test(navigator.userAgent))
      this.gtag('event', 'conversion', { send_to: 'AW-10836102046/Yf-iCIfLwo4DEJ6Xh68o' });

    this.gtagInitialized = true;
  }

  async initHighlightListener() {
    if (botAgentRegex.test(navigator.userAgent) || typeof window === 'undefined' || environment.name === 'development')
      return;

    if (!Router.router) await waitForRouter();
    Router.events.on('routeChangeStart', (fullPath: string) => {
      const path = new URL(`https://bedsforkids.org${fullPath}`).pathname.replace(/\/+$/, '');

      if (!this.highlightInitialized && shouldHighlightBeActive(path)) void this.initHighlight();
      else if (this.h && shouldHighlightBeActive(path) && this.h.getRecordingState() !== 'Recording')
        this.startRecording();
      else if (this.h && !shouldHighlightBeActive(path) && this.h.getRecordingState() === 'Recording')
        this.stopRecording();
    });

    if (!this.highlightInitialized && shouldHighlightBeActive(Router.pathname)) void this.initHighlight();
  }

  async initHighlight() {
    if (botAgentRegex.test(navigator.userAgent) || this.highlightInitialized) return;
    this.highlightInitialized = true;

    const { H } = await import('highlight.run');

    const { sessionSecureID } = H.init(environment.highlight.projects.web, {
      serviceName: 'web',
      tracingOrigins: [environment.urls.api],
      backendUrl: `${environment.highlight.urls.backend}/public`,
      environment: environment.name,
      version: environment.version,

      networkRecording: { enabled: true, recordHeadersAndBody: true, urlBlocklist: [] },
      enablePromisePatch: true,

      disableConsoleRecording: false,
      consoleMethodsToRecord: undefined,
      reportConsoleErrors: false,

      enableCanvasRecording: false,
      enablePerformanceRecording: true,

      recordCrossOriginIframe: false,
      disableBackgroundRecording: false,
      disableSessionRecording: false,

      sendMode: 'webworker',
      privacySetting: 'none',
      storageMode: 'localStorage',
    } satisfies HighlightOptions) || { sessionSecureID: '' };

    if (sessionSecureID) Cookie.set('sessionSecureID', sessionSecureID);
    this.h = H;

    void this.identifyUser();
  }

  startRecording() {
    if (!this.h) return;
    this.h.start();

    void this.identifyUser();
  }

  stopRecording() {
    if (!this.h) return;
    this.h.stop();
  }

  async identifyUser() {
    if (!this.h) return;
    if (!localStorage.getItem(storage.loggedIn)) return;

    const { useUser } = await import('state');
    if (!useUser) return;
    const user = useUser.getState().user;
    if (!user) return;

    this.h.identify(user.email, {
      id: user.id,
      highlightDisplayName: `${user.fname} ${user.lname}`,
      fname: user.fname,
      lname: user.lname,
      email: user.email,
      phone: user.phone,
      avatar: user.image ? `${environment.urls.content}/${user.image}` : undefined,

      // role handling
      ...user.roles.reduce((acc, role) => {
        acc[role] = true;
        return acc;
      }, {}),
    });
  }

  static getInstance(): B4KTracker {
    if (typeof window === 'undefined') return;

    if (!B4KTracker.instance && window.tracker) B4KTracker.instance = window.tracker;
    if (!B4KTracker.instance) B4KTracker.instance = new B4KTracker();
    if (!window.tracker) window.tracker = B4KTracker.instance;

    return B4KTracker.instance;
  }
}

const shouldHighlightBeActive = (path: string) => {
  if (path === '/donate' || path === '/contact') return true;
  if (path === '/login' || path === '/register') return true;
  if (path.startsWith('/admin')) return true;
  if (path.startsWith('/dashboard')) return true;
  if (path.startsWith('/referrals')) return true;
  if (path.startsWith('/staff')) return true;

  return false;
};

const waitForRouter = () => {
  return new Promise<void>(resolve => {
    const interval = setInterval(() => {
      if (!Router.router) return;
      clearInterval(interval);
      resolve();
    }, 10);
  });
};

const tracker = B4KTracker.getInstance();
export { tracker };

export function TrackingScripts() {
  switch (environment.name) {
    case 'production':
      return initProdTrackingScripts();
    case 'staging':
      return initStagingTrackingScripts();
    default:
      return initDevTrackingScripts();
  }

  function initProdTrackingScripts() {
    return (
      <>
        <Script
          src="https://t.bedsforkids.org/b4kdetails"
          async
          defer
          data-website-id="d7a74393-fc61-42a1-9662-007a8fcdcc7c"
          data-cache="true"
          onLoad={() => tracker.initUmami()}
        />
        <Script
          src="https://www.googletagmanager.com/gtag/js?id=G-WGMXKHWYT4"
          async
          defer
          onLoad={() => tracker.handleGtagLoad()}
        />
      </>
    );
  }

  function initStagingTrackingScripts() {
    return (
      <>
        <Script
          src="https://t.bedsforkids.org/b4kdetails"
          async
          defer
          data-website-id="bac39332-a062-4fdc-81f3-f000ebd242a8"
          data-cache="true"
          onLoad={() => tracker.initUmami()}
        />
      </>
    );
  }

  function initDevTrackingScripts() {
    return (
      <>
        {/* <Script
          src="https://t.bedsforkids.org/b4kdetails"
          async
          defer
          data-website-id="bac39332-a062-4fdc-81f3-f000ebd242a8"
          data-cache="true"
          onLoad={() => tracker.initUmami()}
        />
        <Script
          src="https://www.googletagmanager.com/gtag/js?id=G-WGMXKHWYT4"
          async
          defer
          onLoad={() => tracker.handleGtagLoad()}
        /> */}
      </>
    );
  }
}
